import React, {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import actions from "../../actions";
import {FI, Validators} from "../../constants/sharedConstants";
import ErrorMessage from "../custom/containers/ErrorMessage";
import {withSidebar} from "../hoc/withSidebar";
import {
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  HStack,
  ResponsiveBox,
  Typography,
  VStack,
} from "../primitives";
import ERPSBaseButton from "../custom/inputs/ERPSBaseButton";
import DialogHeader from "../custom/inputs/ERPSBaseModelDialog";
import ERPSBaseTextField from "../custom/inputs/ERPSBaseTextField";
import {checkFieldValue} from "../../utils/helper";


const AUTHENTICATE_FIELDS = ["billerId", "creditTransitNumber", "creditAccountNumber"];
const INITIAL_AUTHENTICATE_VALUES = {
  billerId: "",
  creditTransitNumber: "",
  creditAccountNumber: "",
};
const INITIAL_AUTHENTICATE_ERROR_STATE = {
  message: "",
  details: "",
  fields: {},
};

/**
 * Linked FI config for Profile Linking list/authenticate.
 * Credential matching is done server-side (BMO: fi_biller_id; BNS/CIBC: biller_fi_number).
 */
const BCR_CONFIG = [
  {
    fiNumber: FI.BMO,
    label: "BMO",
    nameField: "bmoBillerName",
    billerIdField: "bmoBillerId",
    authenticatedField: "isBmoAuthenticated",
  },
  {
    fiNumber: FI.BNS,
    label: "BNS",
    nameField: "bnsBillerName",
    billerIdField: "bnsBillerId",
    authenticatedField: "isBnsAuthenticated",
  },
  {
    fiNumber: FI.CIBC,
    label: "CIBC",
    nameField: "cibcBillerName",
    billerIdField: "cibcBillerId",
    authenticatedField: "isCibcAuthenticated",
  },
];

const AUTHENTICATE_FIELD_CONFIG = [
  {
    name: "billerId",
    label: "erps.billermanagement.label.billerId",
    validator: Validators.alphanumeric,
  },
  {
    name: "creditTransitNumber",
    label: "erps.billermanagement.crossreference.profile.creditTransitNumber",
    validator: Validators.numeric,
  },
  {
    name: "creditAccountNumber",
    label: "erps.billermanagement.crossreference.profile.creditAccountNumber",
    validator: Validators.alphanumeric,
  },
];

const toStringSafe = (value) => {
  if (value == null) {
    return "";
  }
  return String(value).trim();
};

const getSearchPayload = (user) => {
  const fiNumber = Number(user?.fiNumber);

  if (fiNumber === FI.CTB_BILLER) {
    return {
      centralizedBillerId : user?.billerCrossReferenceDto?.centralizedBillerId
    };
  }

  const currentFi = BCR_CONFIG.find((bcrConfig) => bcrConfig.fiNumber === fiNumber);
  if (!currentFi) {
    return null;
  }

  const billerId = user?.billerNonVolatileDto?.biller_id;
  if (!billerId) {
    return null;
  }

  return {
    [currentFi.billerIdField]: billerId,};
};

const mapInstitutions = (rows = []) => {
  const linkedMap = new Map();

  rows.forEach((row) => {
    BCR_CONFIG.forEach((bcrConfig) => {

      const billerId = row?.[bcrConfig.billerIdField];
      const hasBillerId = toStringSafe(billerId) !== "";
      const alreadyHasFi = [...linkedMap.values()].some(
        (item) => item.fiNumber === bcrConfig.fiNumber
      );

       // Skip null when this FI already has an entry
       if (!hasBillerId && alreadyHasFi) {
        return;
      }
      const mapKey = `${bcrConfig.label}-${billerId}`;
      if (!linkedMap.has(mapKey)) {
        linkedMap.set(mapKey, {
          bcrId: row.id,
          fiNumber: bcrConfig.fiNumber,
          institutionLabel: bcrConfig.label,
          billerName : row?.[bcrConfig.nameField],
          billerId,
          isAuthenticated: row?.[bcrConfig.authenticatedField],
        });
      }
    });
  });

  return Array.from(linkedMap.values());
};

function ProfileLinking(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const loginUser = useSelector((state) => state.loginUserReducer?.user);
  const bcrResponse = useSelector((state) => state.billerCrossReferenceReducer);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const errorRef = useRef(null);
  const [openDialog, setOpenDialog] = useState({
    isOpen: false,
    data: null,
  });
  const [authenticateValues, setAuthenticateValues] = useState(INITIAL_AUTHENTICATE_VALUES);
  const [authenticateError, setAuthenticateError] = useState(INITIAL_AUTHENTICATE_ERROR_STATE);

  useEffect(() => {
    if (errorRef.current) {
      errorRef.current.focus();
    }
  }, [errorMessage]);

  const loadLinkedInstitutions = () => {
    const searchPayload = getSearchPayload(loginUser);

    setErrorMessage(null);
    setLoading(true);

    dispatch(
      actions.billerCrossReferenceActions.searchLinkedInstitutionsForProfileLinking(
        searchPayload,
        {
          history: props.history,
          setLoading,
          setErrorMessage,
        }
      )
    );
  };

  useEffect(() => {
    loadLinkedInstitutions();
  }, []);

  const linkedInstitutions = useMemo(() => {
    const responseRows = bcrResponse?.bcrData?.data || [];
    return mapInstitutions(responseRows);
  }, [bcrResponse?.bcrData?.data]);

  const closeAuthenticateDialog = () => {
    setOpenDialog({ isOpen: false, data: null });
    setAuthenticateValues(INITIAL_AUTHENTICATE_VALUES);
    setAuthenticateError(INITIAL_AUTHENTICATE_ERROR_STATE);
    setSubmitting(false);
  };

  const handleCancelDialog = () => {
    if (submitting) {
      return;
    }
    closeAuthenticateDialog();
  };

  const openAuthenticateDialog = (institution) => {
    setOpenDialog({ isOpen: true, data: institution });
    setAuthenticateValues(INITIAL_AUTHENTICATE_VALUES);
    setAuthenticateError(INITIAL_AUTHENTICATE_ERROR_STATE);
    setSubmitting(false);
  };

  const validateAuthenticateValues = (values) => {
    const errorMessages = [];
    const fieldHighlights = {};
    const trimmedValues = {
      billerId: toStringSafe(values.billerId),
      creditTransitNumber: toStringSafe(values.creditTransitNumber),
      creditAccountNumber: toStringSafe(values.creditAccountNumber),
    };

    AUTHENTICATE_FIELD_CONFIG.forEach(
        ({ name, label, validator}) => {

          const errorMsg = checkFieldValue(
              trimmedValues[name],
              label,
              true,
              validator
          );
          if (errorMsg) {
            errorMessages.push(errorMsg);
            fieldHighlights[name] = true;
          }
        });

    return { errorMessages, fieldHighlights, trimmedValues };
  };

  const handleValueChange = (fieldName) => (event) => {
    const fieldValue = event?.target?.value || "";

    setAuthenticateValues((previousValues) => ({
      ...previousValues,
      [fieldName]: fieldValue,
    }));
    setAuthenticateError(INITIAL_AUTHENTICATE_ERROR_STATE);
  };

  const handleAuthenticateClick = () => {
    if (submitting) { // this is for the dialog, loading is used for the main page
      return;
    }

    const institution = openDialog?.data;
    const { errorMessages, fieldHighlights, trimmedValues } = validateAuthenticateValues(authenticateValues);
    const firstInvalidField = AUTHENTICATE_FIELDS.find((fieldName) => Boolean(fieldHighlights[fieldName]));

    if (errorMessages.length) {
      setAuthenticateError({
        message: errorMessages,
        details: "",
        fields: fieldHighlights,
      });
      const fieldElement = document.getElementById(firstInvalidField);
      if (fieldElement) {
        fieldElement.focus();
      }
      return;
    }

    const searchPayload = getSearchPayload(loginUser);

    setAuthenticateError(INITIAL_AUTHENTICATE_ERROR_STATE);
    setSubmitting(true);
    dispatch(
      actions.billerCrossReferenceActions.authenticateLinkedBiller(
        {
          ...searchPayload,
          bcrId : institution.bcrId,
          fiNumber: institution.fiNumber,
          billerId: trimmedValues.billerId,
          creditTransitNumber: trimmedValues.creditTransitNumber,
          creditAccountNumber: trimmedValues.creditAccountNumber,
        },
        {
          history: props.history,
          onSuccess: (response) => {
            setSubmitting(false);
            const matched = Boolean(response?.data?.matched);
            if (matched) {
              closeAuthenticateDialog();
            } else {
              // List row is patched in the reducer (authenticated -> X); no silent refetch.
              setAuthenticateError({
                message: t(
                  "erps.profilelinking.authenticate.failed",
                  "Authentication Failed"
                ),
                details: t("erps.profilelinking.authenticate.failed.details", {
                  fiName: institution.institutionLabel,
                }),
                fields: {},
              });
            }
          },
          onError: (apiError) => {
            setSubmitting(false);
            setAuthenticateError({
              message: apiError || t(
                "erps.profilelinking.authenticate.failed",
                "Authentication Failed"
              ),
              details: "",
              fields: {},
            });
          },
        }
      )
    );
  };

  const getDialogContent = () => {
    if (!openDialog?.data) {
      return null;
    }

    const billerData = openDialog.data;

    return (
      <ResponsiveBox sx={{ width: "100%", minWidth: 0, maxWidth: "100%" }}>
        <Typography
          aria-describedby="dialog-message"
          className="authenticate-dialog-description"
        >
          {t("erps.profilelinking.authenticate.dialog.description", {
            fiName: billerData.institutionLabel,
          })}
        </Typography>

        <VStack
          spacing={2}
          alignItems="stretch"
          sx={{width: "100%", minWidth: 0 }}
        >
          {authenticateError.message && (
            <ResponsiveBox
              className="manageUsers__errorMsg__container authenticate-dialog-error-banner"
              role="alert"
              sx={{ width: "100%", minWidth: 0 }}
            >
              <ErrorMessage error={authenticateError.message} mt={0} role="alert"/>
            </ResponsiveBox>
          )}

          <ERPSBaseTextField
            id="billerId"
            type="text"
            label={t("erps.billermanagement.label.billerId", "Biller ID")}
            className="three_column__textField"
            size="small"
            required="true"
            value={authenticateValues.billerId}
            onChange={handleValueChange("billerId")}
            error={Boolean(authenticateError.fields?.billerId)}
            isDisabled={submitting}
          />
          <ERPSBaseTextField
            id="creditTransitNumber"
            type="text"
            label={t(
              "erps.billermanagement.crossreference.profile.creditTransitNumber"
            )}
            className="three_column__textField"
            size="small"
            required="true"
            value={authenticateValues.creditTransitNumber}
            onChange={handleValueChange("creditTransitNumber")}
            error={Boolean(authenticateError.fields?.creditTransitNumber)}
            isDisabled={submitting}
          />
          <ERPSBaseTextField
            id="creditAccountNumber"
            type="text"
            label={t(
              "erps.billermanagement.crossreference.profile.creditAccountNumber"
            )}
            className="three_column__textField"
            size="small"
            required="true"
            value={authenticateValues.creditAccountNumber}
            onChange={handleValueChange("creditAccountNumber")}
            error={Boolean(authenticateError.fields?.creditAccountNumber)}
            isDisabled={submitting}
          />

          {authenticateError.details && (
              <>
            <Typography
              variant="body2"
              className="manageUsers__errorMsg__text"
              role="alert"
              sx={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
            >
              {authenticateError.details}
            </Typography>

          <ResponsiveBox
              className="authenticate-dialog-help"
              role="alert"
              sx={{ width: "100%", minWidth: 0 }}
          >
            <Typography
                variant="body2"
                sx={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
            >
              {t("erps.profilelinking.page.authentication.failed.warning", {
                fiName: billerData.institutionLabel,
              })}
            </Typography>
          </ResponsiveBox>
              </>
          )}
        </VStack>
      </ResponsiveBox>
    );
  };

  /* Profile linking page */
  return (
    <div role="main">
      <ResponsiveBox ml={3}>
        <VStack spacing={3} alignItems="stretch">
          {errorMessage && (
            <ResponsiveBox
              className="manageUsers__errorMsg__container"
              role="alert"
            >
              <ErrorMessage error={errorMessage} errorRef={errorRef} />
            </ResponsiveBox>
          )}

          <Typography variant="h1">
            {t(
              "erps.navbar.myprofile.subtitle.profilelinking",
              "Profile linking"
            )}
          </Typography>

          <Typography variant="body1">
            {t(
              "erps.profilelinking.page.description",
              "This Biller profile is registered at more than one institution. You can view another institution details only after you authenticate it by entering that institutions biller profile details."
            )}
          </Typography>

          <Typography variant="h2">
            {t(
              "erps.profilelinking.section.linkedInstitutions",
              "Linked Institutions"
            )}
          </Typography>

          <VStack spacing={2} alignItems="stretch">
            {loading ? (
              <HStack spacing={1.5} alignItems="center">
                <CircularProgress size={24} />
                <Typography variant="body1">
                  {t("erps.common.message.loading", "Loading...")}
                </Typography>
              </HStack>
            ) : linkedInstitutions.length === 0 ? (
              <Typography variant="body1">
                {t(
                  "erps.profilelinking.empty.linkedInstitutions",
                  "No linked institutions found."
                )}
              </Typography>
            ) : (
              linkedInstitutions.map((institution) => {
                const hasProfile = Boolean(institution.billerId);
                const isBillerAuthenticated = institution.isAuthenticated === "Y";
                const isAuthenticationFailed = institution.isAuthenticated === "X";
                const showAuthenticateAction = hasProfile && !isBillerAuthenticated;
                const authenticateButtonLabel = isAuthenticationFailed
                  ? t(
                      "erps.profilelinking.button.reauthenticate",
                      "Re-authenticate"
                    )
                  : t(
                      "erps.profilelinking.button.authenticate",
                      "Authenticate"
                    );
                const profileDisplayValue = hasProfile
                  ? (institution.billerName || "-").toUpperCase()
                  : t(
                      "erps.profilelinking.label.profileNotAvailable",{
                          fiName : institution.institutionLabel}
                    );

                return (
                  <ResponsiveBox
                    key={`${institution.institutionLabel}-${
                      institution.billerId}"
                    }`}
                    className="common__responsive__actionCard"
                  >
                    <Card variant="outlined">
                      <CardContent>
                        <HStack
                          direction="row"
                          className="common__responsive__actionCardRow"
                          alignItems="flex-start"
                          justifyContent="space-between"
                          spacing={2}
                          useFlexGap
                          flexWrap="wrap"
                        >
                          <HStack>
                            <ResponsiveBox className="common__responsive__actionCardLabel">
                              <Typography variant="body1">
                                <strong>{institution.institutionLabel}</strong>
                              </Typography>
                            </ResponsiveBox>

                            <VStack spacing={0} alignItems="flex-start">
                              {hasProfile && (
                                <HStack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                  useFlexGap
                                >
                                  <Typography variant="body1">
                                    {t("erps.billermanagement.label.billerName",
                                      "Biller name"
                                    )}
                                    :
                                  </Typography>
                                  {isBillerAuthenticated && (
                                    <Typography component="span"
                                      className="common__status_chip common__status_chip__success"
                                    >
                                      {t("erps.profilelinking.label.authenticated",
                                        "Authenticated"
                                      )}
                                    </Typography>
                                  )}
                                  {isAuthenticationFailed && (
                                    <Typography component="span"
                                      className="common__status_chip common__status_chip__error"
                                    >
                                      {t("erps.profilelinking.label.authenticationFailed",
                                        "Authentication failed"
                                      )}
                                    </Typography>
                                  )}
                                </HStack>
                              )}
                              <Typography>
                                <strong>{profileDisplayValue}</strong>
                              </Typography>
                            </VStack>
                          </HStack>

                          {showAuthenticateAction && (
                            <ERPSBaseButton
                              variant="primary"
                              aria-label={`${authenticateButtonLabel} ${institution.institutionLabel}`}
                              label={authenticateButtonLabel}
                              onClick={() => openAuthenticateDialog(institution)}
                            />
                          )}
                        </HStack>
                      </CardContent>
                    </Card>
                  </ResponsiveBox>
                );
              })
            )}
          </VStack>
        </VStack>
      </ResponsiveBox>

      <Dialog
        open={openDialog?.isOpen}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className="authenticate-dialog"
        fullWidth
        maxWidth="sm"
        onClose={handleCancelDialog}
      >
        <DialogHeader id="alert-dialog-title" className={"authenticate-box-dialog-header"}>
          {t("erps.profilelinking.authenticate.dialog.header", {
            fiName: openDialog.data?.institutionLabel,
          })}
        </DialogHeader>
        <DialogContent
          id="alert-dialog-description"
          className="authenticate-dialog-content"
        >
          {getDialogContent()}
        </DialogContent>
        <DialogActions className="authenticate-box-dialog-buttons">
          <ERPSBaseButton
            variant="secondary"
            className="button_with_zeromargin"
            label={t("erps.common.button.cancel")}
            onClick={handleCancelDialog}
            isDisabled={submitting}
          />
          <ERPSBaseButton
            variant="primary"
            className="button_with_zeromargin"
            label={t(
              "erps.profilelinking.button.authenticate",
              "Authenticate"
            )}
            onClick={handleAuthenticateClick}
            loading={submitting}
            isDisabled={submitting}
          />
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default withSidebar(ProfileLinking);
